import { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';

type Position = 'bottom-left' | 'bottom-right' | 'bottom-center';

interface TriangleProps {
  $position: Position;
}

const Triangle = styled.div<TriangleProps>`
  width: 26px;
  height: 14px;
  position: absolute;
  overflow: hidden;
  top: -14px;
  ${({ $position }) =>
    $position === 'bottom-left' &&
    css`
      left: 12px;
    `}
  ${({ $position }) =>
    $position === 'bottom-center' &&
    css`
      left: 0;
      right: 0;
      margin: auto;
    `}
    ${({ $position }) =>
    $position === 'bottom-right' &&
    css`
      right: 8px;
    `}
  &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    background: ${({ theme }) => theme.colors.white};
    transform: rotate(45deg);
    top: 8px;
    left: 5px;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

interface BoxProps {
  $left: number;
  $top: number;
  $position: Position;
  $width: number;
}

const Box = styled.div<BoxProps>`
  position: fixed;
  width: ${({ $width }) => $width}px;
  padding: 16px;
  z-index: 11;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  top: ${({ $top }) => $top + 30}px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  ${({ $position, $left }) =>
    $position === 'bottom-left' &&
    css`
      left: calc(${$left}px - 7px);
    `}
  ${({ $position, $left, $width }) =>
    $position === 'bottom-center' &&
    css`
      left: calc(${$left}px - ${$width}px / 2 + 19px);
    `}
    ${({ $position, $left, $width }) =>
    $position === 'bottom-right' &&
    css`
      left: calc(${$left}px - ${$width}px + 40px);
    `}
`;

const TooltipHolder = styled.span`
  margin-left: 6px;
  display: inline-block;
  svg {
    transform: translateY(2px);
  }
`;

const TitleText = styled(Text)`
  margin-bottom: 6px;
  svg {
    margin-right: 8px;
    transform: translateY(1px);
  }
`;

const TooltipIconHolder = styled.span`
  cursor: help;
  color: ${({ theme }) => theme.colors.gray};
`;

interface Props {
  className?: string;
  content: string;
  title?: string;
  width?: number;
  position?: Position;
}

const modalRoot = document.getElementById('modals-root');

const Tooltip: FC<Props> = ({
  content,
  className,
  title,
  position = 'bottom-left',
  width = 260,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const ref = useRef(document.createElement('div'));

  const buttonRef = useRef<HTMLButtonElement>(null);

  const componentRef = useRef<HTMLDivElement>(null);

  const domRect = buttonRef.current?.getBoundingClientRect();

  useEffect(() => {
    const callback = () => setShowTooltip(false);
    window.addEventListener('scroll', callback, true);
    return () => {
      window.removeEventListener('scroll', callback, true);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    modalRoot!.appendChild(el);
    return () => {
      modalRoot!.removeChild(el);
    };
  }, []);

  return (
    <TooltipHolder>
      <>
        <TooltipIconHolder
          onMouseEnter={(e) => {
            e.stopPropagation();
            setShowTooltip(true);
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            setShowTooltip(false);
          }}
          ref={buttonRef}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="6" cy="6" r="5.5" stroke="currentColor" />
            <path
              d="M5.5 4.30147V3.375H6.5V4.30147H5.5ZM5.5 8.625V4.91912H6.5V8.625H5.5Z"
              fill="currentColor"
            />
          </svg>
        </TooltipIconHolder>
        {createPortal(
          <>
            {domRect && showTooltip && (
              <Box
                ref={componentRef}
                $left={domRect.x - domRect.width}
                $top={domRect.y + window.scrollY}
                $position={position}
                $width={width}
                className={className}
              >
                <Triangle $position={position} />
                {!!title && (
                  <TitleText $type="caption">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="6"
                        cy="6"
                        r="5.5"
                        fill="#4136CF"
                        stroke="#4136CF"
                      />
                      <path
                        d="M5.5 4.30147V3.375H6.5V4.30147H5.5ZM5.5 8.625V4.91912H6.5V8.625H5.5Z"
                        fill="white"
                      />
                    </svg>
                    {title}
                  </TitleText>
                )}
                <Text $type="smallMedium">{content}</Text>
              </Box>
            )}
          </>,
          ref.current,
        )}
      </>
    </TooltipHolder>
  );
};

export default Tooltip;
